/**
 * Référentiel des cobénéfices du Label Bas-Carbone : définitions, barèmes par
 * type de projet et libellés de score. Aucune donnée de projet, aucun import.
 *
 * Pourquoi ce fichier est séparé de `cobenefices.ts` : ce dernier importe
 * `project-scores.json` (1 Mo). En ESM les imports sont hoistés, donc un
 * composant navigateur qui ne voulait que `getScoreLabel` ou le type
 * `CoBenefice` embarquait le catalogue entier (audit performance A1,
 * 20 septembre 2026). À importer depuis les composants `'use client'`.
 *
 * `cobenefices.ts` réexporte tout ce qui suit pour les appelants existants.
 */

export interface CoBenefice {
  key: string;
  nom: string;
  nomEn: string;
  description: string;
  descriptionEn: string;
  icon: string;
  couleur: string;
}

/**
 * The 5 core co-benefits defined by Label Bas-Carbone
 */
export const COBENEFICES: CoBenefice[] = [
  {
    key: "biodiversite",
    nom: "Biodiversité",
    nomEn: "Biodiversity",
    description:
      "Augmentation de la diversité des espèces végétales et animales sur le territoire du projet. Amélioration des habitats naturels, protection des écosystèmes et création de corridors écologiques pour la faune locale.",
    descriptionEn:
      "Increased diversity of plant and animal species, creation of ecological corridors and habitat restoration.",
    icon: "butterfly",
    couleur: "#2ecc71",
  },
  {
    key: "eau",
    nom: "Eau",
    nomEn: "Water",
    description:
      "Amélioration de la qualité et de la gestion de l'eau, réduction de la pollution, augmentation de l'infiltration et de la recharge des nappes souterraines. Protection des ressources en eau douce et des écosystèmes aquatiques.",
    descriptionEn:
      "Improvement of water quality and management through natural filtration, runoff reduction and aquifer recharge.",
    icon: "droplets",
    couleur: "#3498db",
  },
  {
    key: "sol",
    nom: "Sol",
    nomEn: "Soil",
    description:
      "Augmentation de la matière organique du sol, amélioration de la structure et de la fertilité naturelle. Prévention de l'érosion, augmentation de la capacité de rétention d'eau et de la vie biologique du sol.",
    descriptionEn:
      "Increased soil organic matter, erosion control, improved soil structure and microbial life.",
    icon: "sprout",
    couleur: "#8b6f47",
  },
  {
    key: "paysage",
    nom: "Paysage",
    nomEn: "Landscape",
    description:
      "Valorisation et amélioration de l'attrait paysager du territoire, enrichissement du patrimoine naturel local. Création de paysages diversifiés et harmonieux contribuant à la qualité de vie et à l'identité du territoire.",
    descriptionEn:
      "Enhancement and improvement of the landscape attractiveness of the area, integration into the natural environment.",
    icon: "landscape",
    couleur: "#16a085",
  },
  {
    key: "social",
    nom: "Social",
    nomEn: "Social",
    description:
      "Travail mobilisé sur le terrain pour la mise en œuvre et l'entretien du projet, développement de compétences. Contribution au lien social et à l'économie rurale.",
    descriptionEn:
      "Field work mobilised to implement and maintain the project, skills development, territorial cohesion and community engagement.",
    icon: "users",
    couleur: "#e67e22",
  },
];

export type ProjectType =
  | "boisement"
  | "reboisement"
  | "belle_foret"
  | "grandes_cultures"
  | "carbon_agri"
  | "vergers"
  | "haies"
  | "reduction_intrants"
  | "batiment";

/**
 * Co-benefits scores (0-5) by project type
 * 0 = Non évalué
 * 1 = Impact faible
 * 2 = Impact modéré
 * 3 = Impact significatif
 * 4 = Impact fort
 * 5 = Impact très fort
 */
export const COBENEFICES_PAR_TYPE: Record<ProjectType, Record<string, number>> =
  {
    boisement: {
      biodiversite: 5,
      eau: 3,
      sol: 4,
      paysage: 5,
      social: 2,
    },
    reboisement: {
      biodiversite: 4,
      eau: 3,
      sol: 4,
      paysage: 4,
      social: 3,
    },
    belle_foret: {
      biodiversite: 5,
      eau: 3,
      sol: 4,
      paysage: 5,
      social: 2,
    },
    grandes_cultures: {
      biodiversite: 2,
      eau: 3,
      sol: 4,
      paysage: 2,
      social: 3,
    },
    carbon_agri: {
      biodiversite: 3,
      eau: 3,
      sol: 3,
      paysage: 2,
      social: 2,
    },
    vergers: {
      biodiversite: 4,
      eau: 3,
      sol: 3,
      paysage: 4,
      social: 3,
    },
    haies: {
      biodiversite: 5,
      eau: 4,
      sol: 3,
      paysage: 5,
      social: 2,
    },
    reduction_intrants: {
      biodiversite: 3,
      eau: 5,
      sol: 4,
      paysage: 1,
      social: 2,
    },
    batiment: {
      biodiversite: 1,
      eau: 1,
      sol: 1,
      paysage: 2,
      social: 3,
    },
  };

/**
 * Textual explanation for each score level
 */
export const SCORE_LABELS: Record<number, string> = {
  0: "Non évalué",
  1: "Impact faible",
  2: "Impact modéré",
  3: "Impact significatif",
  4: "Impact fort",
  5: "Impact très fort",
};

/**
 * Textual explanation for each score level (English)
 */
export const SCORE_LABELS_EN: Record<number, string> = {
  0: "Not assessed",
  1: "Low impact",
  2: "Moderate impact",
  3: "Significant impact",
  4: "High impact",
  5: "Very high impact",
};

/**
 * Get the textual explanation for a score
 */
export function getScoreLabel(score: number, locale?: string): string {
  const labels = locale === "en" ? SCORE_LABELS_EN : SCORE_LABELS;
  return labels[score] || (locale === "en" ? "Not assessed" : "Non évalué");
}

